import clsx from 'clsx';
import { Icon } from 'common/icon';

import styles from './styles.module.css';

import { initialValues, model, validationSchema } from './config';

import { useAlert } from 'components';
import { useCreateCasinoComplaintsMutation } from 'hooks/api/complaints';
import { useMeQuery } from 'hooks/api/users';
import { useClickOutside } from 'hooks/useClickOutside';
import { useCallback, useRef } from 'react';
import { useRouter } from 'next/router';

import { InputField } from 'common';
import { Button } from 'common/new-button';
import { Formik, FormikValues } from 'formik';
import { handleMutation } from 'utils/handleMutation';

import { Routes } from 'constants/routes';
import { shortenFileName } from 'utils/file';

type Props = {
  isVisible: boolean;
  setVisible: (isVisible: boolean) => void;
  casino_id: string;
};

export const ComplaintModalCurrentCasino = ({
  isVisible,
  setVisible,
  casino_id,
}: Props) => {
  const alert = useAlert();
  const router = useRouter();
  const modalRef = useRef<HTMLDivElement | null>(null);

  const { data: me } = useMeQuery();
  const [placeComplaint, mutation] = useCreateCasinoComplaintsMutation();
  const isLoading = mutation.isLoading;

  const onSuccess = useCallback(() => {
    alert.success('Complaint successfully added');
  }, [alert]);

  handleMutation({ ...mutation, onSuccess });

  const onSubmit = async (values: FormikValues) => {
    const { subject, message, screenshot } = values;
    if (!subject && !message) {
      alert.error('Please fill fields');
      return;
    }

    if (!me?.user?._id) {
      router.push(Routes.login);
      return;
    }
    try {
      const response = await placeComplaint({
        casinoId: casino_id,
        message: message,
        title: subject,
        parent: null,
        image: screenshot,
      });

      const complaintId = response?._id;

      if (complaintId) {
        router.push(Routes.complaint(complaintId));
      }

      alert.success('Complaint successfully added');
      setVisible(false);
    } catch (error) {
      alert.error('Failed to submit complaint');
    }
  };

  const handleCloseModal = () => {
    setVisible(false);
  };

  useClickOutside({ onClick: handleCloseModal, ref: modalRef });

  if (!isVisible) {
    return null;
  }

  return (
    <div className={clsx(styles.modalBody, styles.modalBodyActive)}>
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({ dirty, values, isSubmitting, submitForm, validateForm }) => (
          <div className={styles.wrapper} ref={modalRef}>
            <div className={styles.top}>
              <h2 className={styles.title}>New complaint</h2>
              <div
                className={styles.closeButton}
                onClick={() => setVisible(false)}
              >
                <Icon.Close width={20} height={20} />
              </div>
            </div>
            <div className={styles.multiTextInput}>
              <InputField {...model.subject} />
            </div>
            <div className={styles.textInput}>
              <InputField {...model.message} />
            </div>
            <div className={styles.actions_background}>
              <div className={styles.actions}>
                <div className={styles.screenshotInput}>
                  <InputField {...model.screenshot} />
                  <div className={styles.wrapperScreenshotButton}>
                    <Icon.Photo width="20px" />
                    <span>
                      {shortenFileName((values?.screenshot as any)?.name)}
                    </span>
                  </div>
                </div>
                <div className={styles.submitButton}>
                  <Button
                    text={isLoading ? 'Loading...' : 'Send'}
                    onClick={() => {
                      validateForm();
                      submitForm();
                    }}
                    disabled={!dirty || isSubmitting}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </Formik>
    </div>
  );
};
